import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { searchItems } from "../../api/masterItems";
import { useBranchScope } from "./branchScope";
import { localName } from "../../lib/localName";

// ⭐ Autocomplete المواد المركزية في بند PO — يفلتر ضمن الفرع.
export const ItemPicker = ({ onPick, testid = "item-picker" }) => {
  const { t, i18n } = useTranslation();
  const { branchId } = useBranchScope();
  const [term, setTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (term.trim().length < 1) { setRows([]); return undefined; }
    const id = setTimeout(() => {
      searchItems(term.trim(), branchId, 20).then((data) => { setRows(data || []); setOpen(true); })
        .catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(id);
  }, [term, branchId]);

  return (
    <div className="relative" data-testid={testid}>
      <div className="flex items-center gap-2 rounded-2xl bg-background px-3 py-2.5 shadow-neu-sm">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input data-testid={`${testid}-input`} value={term} onChange={(e) => setTerm(e.target.value)}
               placeholder={t("purchases.items.pickItem")}
               className="min-w-0 flex-1 bg-transparent text-xs font-bold outline-none" />
      </div>
      {open && rows.length ? (
        <div data-testid={`${testid}-menu`}
             className="absolute inset-x-0 top-[calc(100%+0.4rem)] z-50 max-h-64 overflow-y-auto rounded-2xl bg-background p-2 shadow-neu">
          {rows.map((row) => (
            <button key={row.item_id} type="button" data-testid={`${testid}-option-${row.item_id}`}
                    onClick={() => { onPick(row); setTerm(""); setOpen(false); }}
                    className="group flex w-full items-center gap-2 rounded-xl px-3 py-2 text-start transition-colors hover:bg-blue-500 hover:text-white focus:bg-blue-500 focus:text-white focus:outline-none">
              <span className="num font-mono text-[10px] text-muted-foreground group-hover:text-white/80 group-focus:text-white/80">{row.item_code}</span>
              <span className="min-w-0 flex-1 truncate text-xs font-bold">
                {(localName(row)) || row.name_ar}
                {row.brand ? <span className="ms-1 text-[10px] font-normal text-muted-foreground group-hover:text-white/80 group-focus:text-white/80">· {row.brand}</span> : null}
              </span>
              <span className="num font-mono text-[10px] text-muted-foreground group-hover:text-white/80 group-focus:text-white/80">
                {row.last_purchase_price ?? row.reference_price ?? "—"} {row.currency}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// التعبئة التلقائية للبند من المادة المركزية (Snapshot محاسبي كامل).
export const lineFromItem = (row) => ({
  master_item_id: row.item_id,
  item_code: row.item_code,
  description: row.name_en || row.name_ar,
  description_ar: row.name_ar,
  brand: row.brand || null,
  uom: row.unit || "pcs",
  unit_price: String(row.last_purchase_price ?? row.reference_price ?? "0.00"),
  item_type: row.item_type,
  accounting_category: row.accounting_category,
  asset_account_code: row.asset_account_code || null,
  expense_account_code: row.expense_account_code || null,
  cost_center_id: row.default_cost_center_id || null,
  pricing_mode: row.pricing_mode || "fixed",
  rate_per_ton_km: row.rate_per_ton_km != null ? String(row.rate_per_ton_km) : null,
  is_service: row.item_type === "expense",
  tax_code_key: "ae_standard",
});
